import { Alert, Badge, Button, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";

import { useStartOnboardingCheckout } from "../../api/generated/endpoints/billing/billing";
import type { CheckoutAttribution } from "../../api/generated/model";
import { leaveFor } from "../../platform/browser";
import { ErrorAlert } from "../../platform/ErrorAlert";
import type { OnboardingPlan, PaidContinuation, PlanCycle, PlanReason } from "./plans-types";
import classes from "./plans.module.css";

export interface FirstAdPlansProps extends PaidContinuation {
  reason: PlanReason;
  onClose: () => void;
  plans?: readonly OnboardingPlan[];
  onCheckoutStarted?: (planCode: string, cycle: PlanCycle) => void;
  checkoutEnabled?: boolean;
  checkoutAttribution?: () => CheckoutAttribution;
}

const dollars = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(cents / 100);

/** The owner's new plans screen. Catalog configuration is held until its rows are verified. */
export function FirstAdPlans({
  teamId, brandId, templateId, reason, onClose, plans = [], onCheckoutStarted,
  checkoutEnabled = false, checkoutAttribution,
  onContinueToApp,
}: FirstAdPlansProps) {
  const [cycle, setCycle] = useState<PlanCycle>("month");
  const [selected, setSelected] = useState(plans.find((plan) => plan.code === "plus")?.code ?? plans[0]?.code);
  const activePlan = plans.some((plan) => plan.code === selected) ? selected
    : plans.find((plan) => plan.code === "plus")?.code ?? plans[0]?.code;
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  const checkout = useStartOnboardingCheckout();
  const busy = checkout.isPending || checkout.isSuccess;
  const choose = (plan: OnboardingPlan) => checkout.mutate(
    { teamId, brandId, data: { plan_code: plan.code, cycle, template_id: templateId,
      ...(checkoutAttribution === undefined ? {} : { attribution: checkoutAttribution() }) } },
    { onSuccess: ({ url }) => { onCheckoutStarted?.(plan.code, cycle); leaveFor(url); } },
  );
  return (
    <Stack className={classes["screen"]} gap="lg">
      <Group justify="space-between">
        <Title order={1}>{reason === "another-ad" ? "Your free ad is used" : "Choose your plan"}</Title>
        <Button variant="subtle" onClick={onClose}>Back to your ad</Button>
      </Group>
      {onContinueToApp ? <Button variant="subtle" onClick={() => onContinueToApp()}>Continue to app</Button> : null}
      <Text>Pick a plan to keep making ads. Every template opens.</Text>
      {checkout.isError ? <ErrorAlert error={checkout.error} /> : null}
      {plans.length === 0 ? (
        <Alert title="Plans are not available yet">Please try again later.</Alert>
      ) : <>
        {!checkoutEnabled ? <Alert title="Checkout is not available yet">Please try again later.</Alert> : null}
        <SegmentedControl aria-label="Billing cycle" value={cycle} disabled={busy}
          data={[{ label: "Monthly", value: "month" }, { label: "Annually", value: "year" }]}
          onChange={(value) => setCycle(value === "year" ? "year" : "month")} />
        <fieldset className={classes["tabs"]} aria-label="Plans">
          {plans.map((plan) => <Button key={plan.code} variant={activePlan === plan.code ? "filled" : "subtle"}
            aria-pressed={activePlan === plan.code} onClick={() => setSelected(plan.code)}>{plan.name}</Button>)}
        </fieldset>
        <div className={classes["cards"]}>
          {plans.map((plan) => {
            const features = [`Up to ~${Math.floor(plan.creditsPerMonth / 43)} AI videos`, ...plan.features];
            const all = expanded.includes(plan.code);
            return <section key={plan.code} aria-label={plan.name}
              className={`${classes["card"]} ${activePlan === plan.code ? classes["selected"] : ""} ${all ? classes["expanded"] : ""}`}>
              {plan.badge ? <Badge>{plan.badge}</Badge> : null}
              <Title order={2}>{plan.name}</Title><Text>{plan.description}</Text>
              <Text size="xl" fw={700}>{dollars(cycle === "month" ? plan.monthlyPriceCents : plan.yearlyPriceCents / 12)} /month</Text>
              <Text size="sm">{cycle === "year" ? `${dollars(plan.yearlyPriceCents)} billed yearly` : "Billed monthly"}</Text>
              <Button loading={busy && checkout.variables?.data.plan_code === plan.code} disabled={busy || !checkoutEnabled}
                onClick={() => choose(plan)}>Choose {plan.name}</Button>
              <ul className={classes["features"]}>{features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
              {features.length > 5 ? <Button className={classes["more"]} variant="subtle" aria-expanded={all}
                onClick={() => setExpanded(all ? expanded.filter((code) => code !== plan.code) : [...expanded, plan.code])}>
                {all ? "Show fewer features" : "Show all features"}</Button> : null}
            </section>;
          })}
        </div>
      </>}
    </Stack>
  );
}
